/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/camelcase */
import { t } from 'i18next'
import { List, Map } from 'immutable'
import * as UriJs from 'uri-js'
import { Option, loadingAsyncState, none, some, unloadedAsyncState } from 'widgets-for-react'
import { BlockData } from '../cms/blocks/types.gen'
import { linkWrapper, parseLinkProps } from '../cms/blocks/utils.gen'
import { DownloadProps } from '../cms/blocks/widgets/downloadBlock.gen'
import { ImageGalleryImageProps, ListWithAtLeast3Items } from '../cms/blocks/widgets/imageGalleryBlock.gen'
import { LinkProps } from '../cms/blocks/widgets/linkBlock.gen'
import { LinkGridItem } from '../cms/blocks/widgets/linkGridBlock.gen'
import { getFormState } from '../form/api'
import { CustomBlockData } from '../publicSite/blocks/custom_blocks/types.mod'
import { ctaBlockProps } from '../publicSite/blocks/custom_blocks/widgets/ctaBlock.mod'
import { expandProps } from '../publicSite/blocks/custom_blocks/widgets/expandBlock.mod'
import { latestUpdatesBlockProps } from '../publicSite/blocks/custom_blocks/widgets/latestUpdatesBlock.mod'
import { PressReleaseItem } from '../publicSite/blocks/custom_blocks/widgets/pressReleasesBlock'
import { quotesBlockProps } from '../publicSite/blocks/custom_blocks/widgets/quotesBlock.mod'
import { timelineItem } from '../publicSite/blocks/custom_blocks/widgets/timelineBlock.mod'
import { CardsBlockProps } from '../publicSite/blocks/custom_blocks/widgets/cardsBlock.mod'
import {
  ctaBlockClasses,
  customSpacerBlockClasses,
  downloadBlockClasses,
  expandBlockClasses,
  factsAndFiguresBlockClasses,
  gridHighlightBlockClasses,
  hubspotIframeBlockClasses,
  iframeBlockClasses,
  imageGalleryBlockClasses,
  latestUpdatesBlockClasses,
  linkGridBlockClasses,
  linkGridBlockPlainClasses,
  linkListBlockClasses,
  quoteBlockNestedClasses,
  quotesBlockClasses,
  reportClasses,
  textBlockClasses,
  textBlockNestedClasses,
  textImageClasses,
  cardsBlockClasses
} from '../publicSite/blocks/style.mod'
import { getOverview } from '../publicSite/storyOverview/api'
import {
  gallery_src,
  gallery_srcset,
  get_default_data_src,
  get_default_data_srcset,
  linkgridblock_src,
  linkgridblock_srcset
} from '../publicSite/utils/extra_images.mod'
import { getVacancyIdFromUrl, titleSplit } from '../publicSite/utils/helpers'
import { getLangFromUrl } from '../publicSite/utils/history.mod'
import { getPressReleases } from './api.mod'
import { GetCookiebarButton, GetCookiebarContent } from './rendering/cookiebar_utils.mod'
import { CustomBlock } from './types_and_parser/custom_blocks.mod'
import {
  Block,
  DownloadBlock,
  IframeBlock,
  ImageGalleryBlock,
  LinkGridBlock,
  LinkGridBlockPlain,
  LinkListBlock,
  QuoteBlockNested,
  TextBlockNested,
  TextReadMoreBlock
} from './types_and_parser/standard_blocks.gen'
import { ParsedMediaImage, ParsedNodeStory, ParsedParagraph } from './types_and_parser/types'
import { ImageWithExpandablesBlockItem } from '../publicSite/blocks/custom_blocks/widgets/imageWithExpandablesBlock'

export const TransformStandardBlock = (b: ParsedParagraph): Block => {
  // @ts-ignore
  return { ...b, kind: b.bundle }
}

export const blockToBlockData = (block: Block | CustomBlock, index: number): Option<BlockData | CustomBlockData> => {
  switch (block.kind) {
    case 'text_nested': {
      return some<BlockData>(textBlockNested(block, index))
    }
    case 'text_with_read_more': {
      return some<BlockData>(textReadMoreBlock(block, index))
    }
    case 'downloads': {
      return some<BlockData>(downloadBlock(block, index))
    }
    case 'links_list': {
      return some<BlockData>(linkListBlock(block, index))
    }
    case 'grid_blocks_plain': {
      return some<BlockData>(linkGridBlockPlain(block, index))
    }
    case 'quote_nested': {
      return some<BlockData>(quoteBlockNested(block, index))
    }
    case 'grid_blocks_image': {
      return some<BlockData>(linkGridBlock(block, index))
    }
    case 'image_gallery': {
      return some<BlockData>(imageGalleryBlock(block, index))
    }
    case 'iframe': {
      return some<BlockData>(iframeBlock(block, index))
    }
    default: {
      const maybe_res = customBlockToWidget(block, index)
      if (maybe_res.kind == 'r') return maybe_res
      console.warn('Unsupported block.', block)
      return none<BlockData>()
    }
  }
}

const customBlockToWidget = (c: CustomBlock, block_index: number): Option<CustomBlockData> => {
  if (c.kind == 'personBlock') {
    return some({
      kind: 'personBlock',
      value: [{ block_index: block_index, name: c.value.name, role: 'whatever' }, {}]
    })
  } else if (c.kind == 'form') {
    const vacancyIdOption = getVacancyIdFromUrl()
    return some({
      kind: 'formBlock',
      value: [
        {
          block_index,
          form_id: c.field_form_list,
          state: getFormState([], c.field_form_list, vacancyIdOption.kind == 'r' ? vacancyIdOption.v : '')
        },
        {}
      ]
    })
  } else if (c.kind == 'quote') {
    return some({
      kind: 'quoteBlock',
      value: [
        {
          blockIndex: block_index,
          quote: c.field_quote,
          person: c.field_author_name,
          function: c.field_function,
          background: c.field_background,
          primaryButton:
            c.field_links.isEmpty() || c.field_links.count() <= 1 ? undefined : linkWrapper(c.field_links.get(0)),
          secondaryButton:
            c.field_links.isEmpty() || c.field_links.count() <= 1 ? undefined : linkWrapper(c.field_links.get(1)),
          errors: Map(),
          push: undefined,
          submit: unloadedAsyncState()
        },
        {}
      ]
    })
  } else if (c.kind == 'press_releases') {
    return some({
      kind: 'pressReleasesBlock',
      value: [
        {
          blockIndex: block_index,
          item_limit: c.field_item_limit,
          show_year_filter: c.field_show_year_filter,
          button: !c.field_button ? undefined : linkWrapper(c.field_button),
          press_releases: loadingAsyncState(() => getPressReleases<PressReleaseItem>())
        },
        {}
      ]
    })
  } else if (c.kind == 'media') {
    return some({
      kind: 'mediaBlock',
      value: [
        {
          blockIndex: block_index,
          media: c.field_media,
          caption: c.field_caption,
          background: c.field_background,
          errors: Map(),
          push: undefined,
          submit: unloadedAsyncState(),
          useCookieBar: true,
          cookiePlaceholder: GetCookiebarContent('media'),
          cookieButton: GetCookiebarButton('media'),
          get_default_data_srcset: get_default_data_srcset,
          get_default_data_src: get_default_data_src
        },
        {}
      ]
    })
  } else if (c.kind == 'stories_grid') {
    return some({
      kind: 'storiesBlock',
      value: [
        {
          block_index,
          title: c.field_title,
          link: !c.field_link ? undefined : linkWrapper(c.field_link),
          stories: loadingAsyncState(() => getOverview<ParsedNodeStory>('stories-overview', getLangFromUrl())),
          errors: Map(),
          push: undefined,
          submit: unloadedAsyncState()
        },
        {}
      ]
    })
  } else if (c.kind == 'timeline') {
    return some({
      kind: 'timelineBlock',
      value: [
        {
          blockIndex: block_index,
          title: c.field_title,
          introduction: c.field_introduction,
          items: c.field_items
            .map<timelineItem>((item) => ({
              title: item.field_title,
              introduction: item.field_introduction,
              media: item.field_media,
              field_image: item.field_timeline_background_image ? item.field_timeline_background_image : undefined,
              image: item.field_timeline_background_image
                ? parseImage(item.field_timeline_background_image)
                : undefined,
              image_alt:
                item.field_timeline_background_image && item.field_timeline_background_image.field_media_image.alt
                  ? item.field_timeline_background_image.field_media_image.alt
                  : '',
              image_size: '600x600'
            }))
            .toList(),
          errors: Map(),
          push: undefined,
          submit: unloadedAsyncState()
        },
        {}
      ]
    })
  } else if (c.kind == 'text_with_image') {
    return some({
      kind: 'textImageBlock',
      value: [
        {
          blockIndex: block_index,
          imageLocation: c.field_featured_content_direction == 'image_left' ? 'left' : 'right',
          field_image: c.field_image ? c.field_image : undefined,
          image: c.field_image ? parseImage(c.field_image) : undefined,
          image_alt: c.field_image && c.field_image.field_media_image.alt ? c.field_image.field_media_image.alt : '',
          text: c.field_text,
          backgroundImage: c.field_image_background ? c.field_image_background : undefined,
          title: c.field_title,
          introduction: c.field_body,
          label: c.field_label,
          errors: Map(),
          push: undefined,
          primary_button: c.field_links.isEmpty() ? undefined : linkWrapper(c.field_links.get(0)),
          secondary_button:
            c.field_links.isEmpty() || c.field_links.count() <= 1 ? undefined : linkWrapper(c.field_links.get(1)),
          submit: unloadedAsyncState(),
          get_default_data_srcset: get_default_data_srcset,
          get_default_data_src: get_default_data_src
        },
        textImageClasses(c.field_featured_content_direction == 'image_left' ? 'left' : 'right')
      ]
    })
  } else if (c.kind == 'hubspot_form') {
    return some({
      kind: 'hubspotIFrameBlock',
      value: [
        {
          index: block_index,
          useCookieBar: true,
          cookiePlaceholder: GetCookiebarContent('iframe'),
          cookieButton: GetCookiebarButton('iframe'),
          inview: { threshold: 0.1, triggerOnce: true },
          inputfields: { portalID: c.field_portal_id, formID: c.field_form_id }
        },
        hubspotIframeBlockClasses
      ]
    })
  } else if (c.kind == 'text') {
    return some({
      kind: 'textBlock',
      value: [
        {
          index: block_index,
          title: c.field_title,
          text: c.field_body,
          primary_button: c.field_links.isEmpty() ? undefined : linkWrapper(c.field_links.get(0)),
          link: c.field_links.isEmpty() || c.field_links.count() <= 1 ? undefined : linkWrapper(c.field_links.get(1)),
          link_type: false,
          background: c.field_background,
          inview: { threshold: 0.1, triggerOnce: true }
        },
        textBlockClasses
      ]
    })
  } else if (c.kind == 'grid_highlight') {
    return some({
      kind: 'gridHighlightBlock',
      value: [
        {
          index: block_index,
          title: c.field_title,
          text: c.field_introduction,
          link: c.field_link,
          field_highlight_items: c.field_highlight_items.toArray()
        },
        gridHighlightBlockClasses
      ]
    })
  } else if (c.kind == 'expandable_content_blocks') {
    return some({
      kind: 'expandBlock',
      value: [
        {
          index: block_index,
          title: c.field_title,
          intro: c.field_introduction,
          items: c.field_expandable_content_blocks
            .filter((x) => x !== undefined)
            .map<expandProps>((e, i) => ({
              key: `${block_index}-${i}`,
              index: block_index,
              title: e!.field_title,
              text: e!.field_body,
              links: e!.field_link_list,
              downloads: e!.field_downloads,
              open: false,
              useSVGIcons: true
            }))
            .toArray(),
          link: !c.field_link ? undefined : linkWrapper(c.field_link),
          inview: { threshold: 0.1, triggerOnce: true }
        },
        expandBlockClasses
      ]
    })
  } else if (c.kind == 'reports_overview') {
    return some({
      kind: 'reportsBlock',
      value: [{ block_index: block_index, field_tabs: c.field_tabs, current_tab: 0 }, reportClasses]
    })
  } else if (c.kind == 'cta') {
    console.log(c)
    return some({
      kind: 'ctaBlock',
      value: [
        {
          index: block_index,
          title: c.field_title ? titleSplit(c.field_title) : '',
          intro: c.field_introduction,
          primary_button: c.field_link ? c.field_link && linkWrapper(c.field_link) : undefined,
          link_type: false,
          field_image: c.field_image ? c.field_image : undefined,
          image: c.field_image ? parseImage(c.field_image) : undefined,
          image_alt: c.field_image && c.field_image.field_media_image.alt ? c.field_image.field_media_image.alt : '',
          image_size: '640w',
          text_color: 'white',
          get_default_data_src,
          get_default_data_srcset,
          inview: { threshold: 0.1, triggerOnce: true }
        } as ctaBlockProps,
        ctaBlockClasses(!!c.field_image)
      ]
    })
  } else if (c.kind == 'quotes') {
    return some({
      kind: 'quotesBlock',
      value: [
        {
          index: block_index,
          field_title: c.field_title,
          field_quote_items: c.field_quote_items
            .map((e) => ({
              field_author_name: e!.field_author_name,
              field_function: e!.field_function,
              field_link: e!.field_link,
              field_quote: e!.field_quote,
              field_image: e.field_image ? e.field_image : undefined,
              image: e.field_image ? parseImage(e.field_image) : undefined,
              image_alt:
                e.field_image && e.field_image.field_media_image.alt ? e.field_image.field_media_image.alt : '',
              image_size: '450x450'
            }))
            .toList(),
          inview: {
            threshold: 0.1,
            triggerOnce: true
          }
        } as quotesBlockProps,
        quotesBlockClasses(true)
      ]
    })
  } else if (c.kind == 'latest_updates_block') {
    return some({
      kind: 'latestUpdatesBlock',
      value: [
        {
          index: block_index,
          field_title: c.field_title,
          field_latest_update_item: c.field_latest_update_item
            .map((e) => ({
              field_label: e!.field_label,
              field_link: e!.field_link,
              field_image: e.field_image ? e.field_image : undefined
            }))
            .toList(),
          inview: {
            threshold: 0.1,
            triggerOnce: true
          }
        } as latestUpdatesBlockProps,
        latestUpdatesBlockClasses(true)
      ]
    })
  } else if (c.kind == 'facts_and_figures') {
    return some({
      kind: 'factsAndFiguresBlock',
      value: [
        {
          index: block_index,
          facts_and_figures_items: c.field_facts_and_figures_items,
          inview: { threshold: 0.1, triggerOnce: true }
        },
        factsAndFiguresBlockClasses(false)
      ]
    })
  } else if (c.kind == 'custom_spacer') {
    return some({
      kind: 'customSpacerBlock',
      value: [{ index: block_index, inview: { threshold: 0.1, triggerOnce: true } }, customSpacerBlockClasses(false)]
    })
  } else if (c.kind == 'image_with_expandable') {
    return some({
      kind: 'imageWithExpandablesBlock',
      value: [
        {
          blockIndex: block_index,
          title: c.field_title,
          introduction: c.field_introduction,
          items: (c.field_expandable_item || List())
            .map((e) => ({
              title: e.field_title,
              text: e.field_introduction,
              link: e.field_link && e.field_link.url ? linkWrapper(e.field_link) : undefined,
              field_image: e.field_image,
              image_alt: e.field_image?.field_media_image?.alt || ''
            }))
            .toList()
        },
        {}
      ]
    })
  } else if (c.kind == 'cards') {
    return some({
      kind: 'cardsBlock',
      value: [
        {
          blockIndex: block_index,
          title: c.field_title,
          introduction: c.field_introduction,
          items: c.field_card
            .map((e) => ({
              field_title: e!.field_title,
              field_introduction: e!.field_introduction,
              field_link: e!.field_link
            }))
            .toList(),
          inview: { threshold: 0.1, triggerOnce: true }
        },
        cardsBlockClasses
      ]
    })
  }
  return none()
}

const parseImage = (i: ParsedMediaImage | undefined): number =>
  i != undefined &&
  i.field_media_image != undefined &&
  i.field_media_image.image != undefined &&
  i.field_media_image.image.id != null
    ? i.field_media_image.image.id
    : 0

export const parseMediaUrl = (s: string) => {
  const uri = UriJs.parse(s)
  return `/drupal/media${uri.path}` + (uri.query ? `?${uri.query}` : '')
}

export const textBlockNested = (block: TextBlockNested, index: number): BlockData => ({
  kind: 'textBlockNested',
  value: [
    {
      index: index,
      text: block.field_body,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    textBlockNestedClasses
  ]
})

export const textReadMoreBlock = (block: TextReadMoreBlock, index: number): BlockData => ({
  kind: 'textReadMoreBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      text: block.field_body,
      open: false,
      readmore_blocks: List(block.field_read_more)
        .map(TransformStandardBlock)
        .map((b, i) => blockToBlockData(b, i))
        .map((b) => (b.kind === 'r' ? b.v : undefined))
        .map((b) => {
          if (b.kind === 'textBlockNested') {
            return b
          }
          if (b.kind === 'mediaBlockNested') {
            return b
          }
          if (b.kind === 'quoteBlockNested') {
            return b
          }
          return undefined
        })
        .toList(),
      copy: {
        less: 'Read less',
        more: t('Common:ReadMore')
      },
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    textBlockClasses
  ]
})

export const quoteBlockNested = (block: QuoteBlockNested, index: number): BlockData => ({
  kind: 'quoteBlockNested',
  value: [
    {
      index: index,
      text: block.field_text,
      author: block.field_author_name ? block.field_author_name : '',
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    quoteBlockNestedClasses
  ]
})

export const linkListBlock = (block: LinkListBlock, index: number): BlockData => ({
  kind: 'linkBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      intro: block.field_introduction ? block.field_introduction : '',
      items: block.field_links_list
        .filter((x) => x !== undefined)
        .map<LinkProps>((x, i) => parseLinkProps(x.field_url, '', i))
        .toArray(),
      useSVGIcons: false,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    linkListBlockClasses
  ]
})

export const downloadBlock = (block: DownloadBlock, index: number): BlockData => ({
  kind: 'downloadBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      intro: block.field_introduction ? block.field_introduction : '',
      items: block.field_download_list
        .filter((x) => x !== undefined)
        .map<DownloadProps>((e, i) => ({
          key: `${e!.field_title} ${i}`,
          title: e!.field_title,
          file_url:
            e &&
            e.field_download &&
            e.field_download.field_media_file &&
            e.field_download.field_media_file.file &&
            e.field_download.field_media_file.file.url
              ? parseMediaUrl(e.field_download.field_media_file.file.url)
              : ''
        }))
        .toArray(),
      useSVGIcons: true,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    downloadBlockClasses
  ]
})

export const linkGridBlock = (block: LinkGridBlock, index: number): BlockData => ({
  kind: 'linkGridBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      intro: block.field_introduction ? block.field_introduction : '',
      items: block.field_grid_blocks
        .filter((x) => x !== undefined)
        .map<LinkGridItem>((e) => ({
          ...e,
          image: parseImage(e!.field_image),
          image_size: 'grid',
          image_alt:
            e && e.field_image && e.field_image.field_media_image.alt ? e.field_image.field_media_image.alt : '',
          title: e && e.field_title ? titleSplit(e.field_title) : '',
          intro: e && e.field_introduction ? e.field_introduction : '',
          link:
            e && e.field_link && e.field_link.title && e.field_link.url != null ? linkWrapper(e.field_link) : undefined,
          show_image: block.kind == 'grid_blocks_image' ? 'yes' : 'no'
        }))
        .toArray(),
      link: block.field_link != undefined ? linkWrapper(block.field_link) : undefined,
      show_image: 'yes',
      get_default_data_src: linkgridblock_src,
      get_default_data_srcset: linkgridblock_srcset,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    linkGridBlockClasses
  ]
})

export const linkGridBlockPlain = (block: LinkGridBlockPlain, index: number): BlockData => ({
  kind: 'linkGridBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      intro: block.field_introduction ? block.field_introduction : '',
      items: block.field_grid_blocks
        .map<LinkGridItem>((e) => ({
          ...e,
          image: 0,
          image_size: 'grid',
          image_alt: '',
          title: e && e.field_title ? titleSplit(e.field_title) : '',
          label: e && e.field_label ? e.field_label : '',
          intro: e && e.field_introduction ? e.field_introduction : '',
          link: e && e.field_link && e.field_link.title && e.field_link.url ? linkWrapper(e.field_link) : undefined,
          show_image: 'no'
        }))
        .toArray(),
      link: block.field_link != undefined ? linkWrapper(block.field_link) : undefined,
      show_image: 'yes',
      get_default_data_src: linkgridblock_src,
      get_default_data_srcset: linkgridblock_srcset,
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    linkGridBlockPlainClasses
  ]
})

export const imageGalleryBlock = (block: ImageGalleryBlock, index: number): BlockData => ({
  kind: 'imageGalleryBlock',
  value: [
    {
      index: index,
      title: block.field_title ? titleSplit(block.field_title) : '',
      intro: block.field_introduction ? block.field_introduction : '',
      images: new ListWithAtLeast3Items(
        block.field_image_gallery
          .map<ImageGalleryImageProps>((e) => ({
            image: parseImage(e.field_image),
            image_alt: e.field_image.field_media_image.alt,
            image_size: '1280w',
            is_foreground: false,
            caption: e.field_image_caption ? e.field_image_caption : '',
            source: e.field_image && e.field_image.field_source ? e.field_image.field_source : '',
            index: index,
            imageDataSizes: '(min-width: 1280px) 1280px, 100vw'
          }))
          .toList()
      ),
      imagePreviewCount: 4,
      get_default_data_src: gallery_src,
      get_default_data_srcset: gallery_srcset,
      copy: {
        close: t('Common:ImageGallery.CloseOverlay'),
        more: t('Common:ImageGallery.MoreImages'),
        enlarged: t('Common:ImageGallery.EnlargedImage'),
        previous: t('Common:ImageGallery.PreviousImage'),
        next: t('Common:ImageGallery.NextImage')
      },
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    imageGalleryBlockClasses
  ]
})

export const iframeBlock = (block: IframeBlock, index: number): BlockData => ({
  kind: 'iframeBlock',
  value: [
    {
      index: index,
      link: block.field_link.url,
      useCookieBar: true,
      cookiePlaceholder: GetCookiebarContent('iframe'),
      cookieButton: GetCookiebarButton('iframe'),
      inview: {
        threshold: 0.1,
        triggerOnce: true
      }
    },
    iframeBlockClasses
  ]
})
